/** Shared request/response contract for demo/lambda and demo/web. */

export const DEMO_PASSCODE_HEADER = 'x-demo-passcode' as const;

/** Cheddar outbound player-balance pull (`api/src/services/playerBalances/fetchPlayerBalances.ts`). */
export const PLAYER_BALANCE_SECRET_HEADER =
  'X-Cheddar-Player-Balance-Secret' as const;

export const ErrorCode = {
  INVALID_PASSCODE: 'INVALID_PASSCODE',
  NOT_FOUND: 'NOT_FOUND',
  NOT_IMPLEMENTED: 'NOT_IMPLEMENTED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  BAD_REQUEST: 'BAD_REQUEST',
  INSUFFICIENT_DEMO_BALANCE: 'INSUFFICIENT_DEMO_BALANCE',
  CAP_EXCEEDED: 'CAP_EXCEEDED',
  INVALID_ADDRESS: 'INVALID_ADDRESS',
  /** Cheddar 403 — typically RestApiWhitelistedIp mismatch. */
  CHEDDAR_FORBIDDEN: 'CHEDDAR_FORBIDDEN',
  /** Cheddar 451 — risk-flagged destination / ScoreChain block. */
  CHEDDAR_RISK_BLOCKED: 'CHEDDAR_RISK_BLOCKED',
  /** Idempotency key already used for a different swap. */
  CHEDDAR_IDEMPOTENCY_CONFLICT: 'CHEDDAR_IDEMPOTENCY_CONFLICT',
  /** Request was well-formed but the market cannot route that pair. */
  CHEDDAR_SWAP_UNROUTABLE: 'CHEDDAR_SWAP_UNROUTABLE',
  /** Origin asset list temporarily unavailable. */
  SWAP_TOKENS_UNAVAILABLE: 'SWAP_TOKENS_UNAVAILABLE',
  /** Creating the swap failed for a reason with no more specific code. */
  SWAP_FAILED: 'SWAP_FAILED',
  /** GET /networks failed to load the mint catalog. */
  NETWORKS_UNAVAILABLE: 'NETWORKS_UNAVAILABLE',
  /** ensureCustomerProvisioned / POST /v1/deposit failed. */
  PROVISIONING_FAILED: 'PROVISIONING_FAILED',
  /** mintId not present in GET /v1/mints catalog. */
  UNKNOWN_MINT: 'UNKNOWN_MINT',
  /** Svix signature missing, malformed, wrong, or stale. */
  INVALID_WEBHOOK_SIGNATURE: 'INVALID_WEBHOOK_SIGNATURE',
  /** Player-balance pull secret missing or wrong. */
  INVALID_PLAYER_BALANCE_SECRET: 'INVALID_PLAYER_BALANCE_SECRET',
  // Customer token swap (Cheddar `cheddar/swap-api` error envelope codes).
  /** Org does not have customer swaps enabled. */
  SWAPS_DISABLED: 'SWAPS_DISABLED',
  /** Pair violates a swap rule (same symbol, non-swappable mint, USDC/USDT off Solana). */
  INVALID_PAIR: 'INVALID_PAIR',
  /** amountIn is below Cheddar's swap floor. */
  BELOW_MINIMUM: 'BELOW_MINIMUM',
  /** 1Click cannot currently route this pair/amount. */
  INSUFFICIENT_LIQUIDITY: 'INSUFFICIENT_LIQUIDITY',
  /** Quote provider unavailable — transient, safe to retry. */
  QUOTE_UNAVAILABLE: 'QUOTE_UNAVAILABLE',
  /**
   * The provider refused to quote this exact amount/pair (e.g. a
   * provider-side minimum). Permanent for these inputs — retrying unchanged
   * will fail again; the caller must change the amount or the pair. See
   * `providerMessage` on the error body for the provider's own reason.
   */
  QUOTE_REJECTED: 'QUOTE_REJECTED',
  /**
   * The submit call to Cheddar failed with a 5xx/timeout/network error — it
   * is unknown whether the swap was created. NOT safe to retry (a retry
   * would be a second real swap); the caller should check status instead.
   */
  SWAP_SUBMIT_AMBIGUOUS: 'SWAP_SUBMIT_AMBIGUOUS',
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

export interface ApiErrorBody {
  error: {
    code: ErrorCode | string;
    message: string;
    /** Provider's own sanitized explanation — present only for QUOTE_REJECTED. */
    providerMessage?: string;
  };
}

export type DemoChain =
  | 'BITCOIN'
  | 'BITCOIN_CASH'
  | 'LITECOIN'
  | 'ETHEREUM'
  | 'SOLANA'
  | 'TRON'
  | 'BSC'
  | 'POLYGON'
  | 'ROBINHOOD';

export interface SessionRequest {
  email: string;
}

export interface SessionResponse {
  customerId: string;
  email: string;
}

export interface NetworkToken {
  mintId: number;
  symbol: string;
  decimals: number;
  /** USD dollars as a decimal string (not micro-USD). Cap math: humanAmount × usdPrice. */
  usdPrice: string;
  /** Optional mint icon URL from Cheddar Mint.image. */
  image?: string | null;
  /** Whether this mint can participate in a customer token swap (GET /v1/mints). */
  swappable?: boolean;
}

export interface WithdrawRequest {
  customerId: string;
  mintId: number;
  /** Human-unit decimal string. */
  amount: string;
  toAddress: string;
  /** Optional — same key retries must not double-debit. */
  idempotencyKey?: string;
}

export interface WithdrawResponse {
  transactionId: string;
  status: 'SUBMITTED';
}

export interface NetworkGroup {
  isTestnet: boolean;
  chain: DemoChain | string;
  tokens: NetworkToken[];
}

export interface NetworksResponse {
  networks: NetworkGroup[];
}

export interface EnsureCustomerProvisionedResult {
  customerId: string;
  mintId: number;
  address: string;
}

export interface DepositRequest {
  customerId: string;
  mintId: number;
}

export interface DepositResponse {
  address: string;
  chain: DemoChain | string;
  mintId: number;
  isTestnet: boolean;
}

/** Ledger / account history row exposed to the SPA. */
export type AccountTxType = 'CREDIT' | 'DEBIT' | 'REVERSAL';
export type AccountTxStatus = 'PENDING' | 'CONFIRMED' | 'FAILED' | 'REVERSED';

export interface AccountTransaction {
  id: string;
  type: AccountTxType | string;
  status: AccountTxStatus | string;
  amount: string;
  usdAmount: string;
  mintId: string;
  symbol?: string;
  chain?: string;
  isTestnet?: boolean;
  txHash?: string;
  /** Cheddar scannerUrl passed through — do not rebuild. */
  explorerUrl?: string;
  isFlagged?: boolean;
  /** Hold/failure reason from withdrawal webhooks. */
  statusReason?: string;
  createdAt: string;
}

export interface AccountResponse {
  /** Org DEFAULT main-wallet confirmed balances keyed by mintId (human-unit decimal strings). */
  balances: Record<string, string>;
  /** Per-customer demo ledger balances keyed by mintId (human-unit decimal strings). */
  customerBalances: Record<string, string>;
  transactions: AccountTransaction[];
}

// ---------------------------------------------------------------------------
// Deposit swaps — fund the balance with any token on any chain.
//
// The credit arrives through the ORDINARY CUSTOMER_DEPOSIT webhook, so nothing
// in the ledger or the account view is swap-specific. These types cover only
// the funding half.
// ---------------------------------------------------------------------------

/** An origin asset the customer may send. Always taken from the API, never built. */
export interface SwapToken {
  assetId: string;
  blockchain: string;
  symbol: string;
  decimals: number;
  /** null for a chain's native asset. */
  contractAddress: string | null;
}

export interface SwapTokensResponse {
  tokens: SwapToken[];
}

export interface CreateSwapRequest {
  customerId: string;
  originAssetId: string;
  /** ATOMIC units of the origin asset as a decimal string. Never a float. */
  amount: string;
  /** Address on the ORIGIN chain, refunded if the swap fails. */
  refundTo: string;
}

/** Cheddar's seven intent statuses. Everything but the first two is terminal. */
export type SwapStatus =
  | 'PENDING_DEPOSIT'
  | 'PROCESSING'
  | 'SUCCESS'
  | 'INCOMPLETE_DEPOSIT'
  | 'REFUNDED'
  | 'FAILED'
  | 'EXPIRED';

export const TERMINAL_SWAP_STATUSES: readonly SwapStatus[] = [
  'SUCCESS',
  'INCOMPLETE_DEPOSIT',
  'REFUNDED',
  'FAILED',
  'EXPIRED',
];

export function isTerminalSwapStatus(status: string): boolean {
  return (TERMINAL_SWAP_STATUSES as readonly string[]).includes(status);
}

export interface SwapIntentResponse {
  intentId: string;
  status: SwapStatus | string;
  depositAddress: string;
  /** If non-null this is REQUIRED alongside the address — omitting it loses the funds. */
  depositMemo: string | null;
  /** The real floor. Show this as "send at least", not amountIn. */
  minAmountIn: string | null;
  /** Guaranteed minimum credited. The only guaranteed output number. */
  minAmountOut: string | null;
  amountOutFormatted: string | null;
  deadline: string;
  failureReason: string | null;
  /** An ESTIMATE. Do not promise it. */
  quotedAmountOut?: string | null;
  actualAmountOut?: string | null;
  slippageToleranceBps?: number | null;
}

// ---------------------------------------------------------------------------
// Customer token swap — an existing customer converts one Cheddar-custodied
// balance into another (org DEFAULT main wallet → NEAR 1Click → customer
// wallet). Distinct feature from the deposit swaps above: no name/type
// overlap intended.
//
// The SPA speaks human units throughout, exactly like withdraw and balances —
// the Lambda does all atomic <-> human conversion against the mint's
// `decimals` before/after calling Cheddar's `/v1/customer-swap*` (atomic-unit)
// REST surface.
// ---------------------------------------------------------------------------

export interface CustomerSwapQuoteRequest {
  customerId: string;
  fromMintId: number;
  toMintId: number;
  /** Human-unit decimal string of the source mint. */
  amount: string;
}

export interface CustomerSwapQuoteResponse {
  /** Human-unit decimal string, destination mint. An estimate — do not promise it. */
  estimatedAmountOut: string;
  /** Human-unit decimal string, destination mint. Guaranteed minimum after slippage. */
  minAmountOut: string;
  feeBps: number;
  /** Dollar decimal string. */
  usdValue: string;
  estimatedTimeSeconds: number | null;
}

export interface CreateCustomerSwapRequest {
  customerId: string;
  fromMintId: number;
  toMintId: number;
  /** Human-unit decimal string of the source mint. */
  amount: string;
}

export type CustomerSwapStatus =
  | 'PENDING'
  | 'PREFUNDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED';

export const TERMINAL_CUSTOMER_SWAP_STATUSES: readonly CustomerSwapStatus[] = [
  'COMPLETED',
  'FAILED',
];

export function isTerminalCustomerSwapStatus(status: string): boolean {
  return (TERMINAL_CUSTOMER_SWAP_STATUSES as readonly string[]).includes(status);
}

export interface CustomerSwapResponse {
  swapId: string;
  status: CustomerSwapStatus | string;
  fromMintId: number;
  toMintId: number;
  /** All amounts below are human-unit decimal strings; nulls preserved. */
  amountIn: string;
  estimatedAmountOut: string;
  minAmountOut: string;
  amountOut: string | null;
  originTxHash: string | null;
  destinationTxHash: string | null;
  failureReason: string | null;
  createdAt: string;
  completedAt: string | null;
}
